import { List } from 'immutable';
import { SelectionState, EditorState, Modifier, AtomicBlockUtils, RichUtils, genKey, ContentBlock, BlockMapBuilder, ContentState } from '@wix/draft-js';
import DraftOffsetKey from '@wix/draft-js/lib/DraftOffsetKey';
import { cloneDeepWith, findIndex, findLastIndex, flatMap, debounce, times, countBy } from 'lodash';
import { TEXT_TYPES } from '../consts.js';
import { CUSTOM_LINK_TYPE, LINK_TYPE, SPOILER_TYPE } from 'wix-rich-content-common';
import { getContentSummary } from 'wix-rich-content-common/libs/contentAnalytics';

var __defProp = Object.defineProperty;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __objRest = (source, exclude) => {
  var target = {};
  for (var prop in source)
    if (__hasOwnProp.call(source, prop) && exclude.indexOf(prop) < 0)
      target[prop] = source[prop];
  if (source != null && __getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(source)) {
      if (exclude.indexOf(prop) < 0 && __propIsEnum.call(source, prop))
        target[prop] = source[prop];
    }
  return target;
};
const isEditorState = (value) => (value == null ? void 0 : value.getCurrentContent) && value;
const cloneDeepWithoutEditorState = (obj) => cloneDeepWith(obj, isEditorState);
const draftInlineStyle = {
  bold: "BOLD",
  underline: "UNDERLINE",
  italic: "ITALIC",
  not_bold: "NOT_BOLD",
  not_italic: "NOT_ITALIC",
  not_underline: "NOT_UNDERLINE",
  spoiler: SPOILER_TYPE
};
const getDraftInlineStyle = (inlineStyle) => draftInlineStyle[inlineStyle];
const hasInlineStyle = (inlineStyle, editorState) => {
  const draftInlineStyle2 = getDraftInlineStyle(inlineStyle);
  return editorState.getCurrentInlineStyle().has(draftInlineStyle2);
};
function createSelection({
  blockKey,
  anchorOffset,
  focusOffset
}) {
  return SelectionState.createEmpty(blockKey).merge({
    anchorOffset,
    focusOffset
  });
}
const insertLinkInPosition = (editorState, blockKey, start, end, { url, target, rel }) => {
  const selection = createSelection({ blockKey, anchorOffset: start, focusOffset: end });
  const linkEntityData = createLinkEntityData({
    url,
    target,
    rel
  });
  return insertLink(editorState, selection, linkEntityData);
};
const getEntityData = (editorState) => {
  const selection = getSelection(editorState);
  const contentState = editorState.getCurrentContent();
  const blockKey = selection.getStartKey();
  const block = contentState.getBlockForKey(blockKey);
  const entityKey = block.getEntityAt(selection.getStartOffset());
  if (entityKey) {
    const entity = contentState.getEntity(entityKey);
    const entityData = entity == null ? void 0 : entity.getData();
    return entityData;
  }
  return null;
};
const insertCustomLink = (editorState, customData) => {
  const selection = getSelection(editorState);
  const editorStateWithLink = isSelectionBelongsToExistingLink(editorState, selection) ? updateLink(editorState, selection, { customData }) : insertLink(editorState, selection, { customData });
  const newEditorState = EditorState.forceSelection(
    editorStateWithLink,
    selection.merge({ anchorOffset: selection.getFocusOffset() })
  );
  return newEditorState;
};
const updateLinkAtCurrentSelection = (editorState, data) => {
  const selection = getSelection(editorState);
  const linkEntityData = createLinkEntityData(data);
  const editorStateWithLink = updateLink(editorState, selection, linkEntityData);
  return EditorState.forceSelection(
    editorStateWithLink,
    selection.merge({ anchorOffset: selection.getFocusOffset() })
  );
};
const getBlockAtStartOfSelection = (editorState) => {
  const selectionState = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(selectionState.getStartKey());
  return block;
};
const insertLinkAtCurrentSelection = (editorState, _a) => {
  var _b = _a, { text, defaultName } = _b, entityData = __objRest(_b, ["text", "defaultName"]);
  let selection = getSelection(editorState);
  let newEditorState = editorState;
  let editorStateWithLink, editorStateSelection;
  const linkEntityData = createLinkEntityData(entityData);
  const isExistsLink = isSelectionBelongsToExistingLink(newEditorState, selection);
  if (isExistsLink) {
    editorStateWithLink = updateLink(newEditorState, selection, linkEntityData);
    editorStateSelection = selection.merge({ anchorOffset: selection.getFocusOffset() });
  } else {
    if (selection.isCollapsed()) {
      const { url } = entityData;
      const urlToInsertWhenCollapsed = text ? text : defaultName ? defaultName : url;
      const contentState = Modifier.insertText(
        editorState.getCurrentContent(),
        selection,
        urlToInsertWhenCollapsed
      );
      selection = selection.merge({
        focusOffset: selection.getFocusOffset() + urlToInsertWhenCollapsed.length
      });
      newEditorState = EditorState.push(editorState, contentState, "insert-characters");
    }
    editorStateWithLink = insertLink(newEditorState, selection, linkEntityData);
    editorStateSelection = editorStateWithLink.getCurrentContent().getSelectionAfter();
  }
  return EditorState.forceSelection(editorStateWithLink, editorStateSelection);
};
function isSelectionBelongsToExistingLink(editorState, selection) {
  const startOffset = selection.getStartOffset();
  const endOffset = selection.getEndOffset();
  return getSelectedLinks(editorState).find(({ range }) => {
    return range[0] <= startOffset && range[1] >= endOffset;
  });
}
function updateLink(editorState, selection, linkData) {
  const blockKey = selection.getStartKey();
  const block = editorState.getCurrentContent().getBlockForKey(blockKey);
  const entityKey = block.getEntityAt(selection.getStartOffset());
  return setEntityData(editorState, entityKey, linkData);
}
function preventLinkInlineStyleForFurtherText(editorState, selection) {
  const focusOffset = selection.getFocusOffset();
  const selectionForSpace = createSelection({
    blockKey: selection.getAnchorKey(),
    anchorOffset: focusOffset,
    focusOffset
  });
  return Modifier.insertText(editorState.getCurrentContent(), selectionForSpace, " ");
}
function insertLink(editorState, selection, data) {
  const oldSelection = editorState.getSelection();
  const type = (data == null ? void 0 : data.customData) ? CUSTOM_LINK_TYPE : LINK_TYPE;
  const editorWithLink = addEntity(editorState, selection, {
    type,
    data,
    mutability: "MUTABLE"
  });
  const contentWithLink = editorWithLink.getCurrentContent();
  const selectedTextLength = contentWithLink.getBlockForKey(oldSelection.getAnchorKey()).getText().length;
  const shouldPreventInlineStyleAtCurrentBlock = selectedTextLength - selection.getFocusOffset() === 0;
  const isNewLine = selection.getAnchorKey() !== oldSelection.getAnchorKey();
  const preventInlineStyle = shouldPreventInlineStyleAtCurrentBlock || isNewLine;
  const contentState = preventInlineStyle ? preventLinkInlineStyleForFurtherText(editorWithLink, selection) : contentWithLink;
  const selectionAfter = isNewLine ? contentWithLink.getSelectionAfter() : contentState.getSelectionAfter();
  return EditorState.push(
    editorState,
    Modifier.applyInlineStyle(contentState, selection, "UNDERLINE").set(
      "selectionAfter",
      selectionAfter
    ),
    "change-inline-style"
  );
}
function createLinkEntityData({ url, anchor, target, rel }) {
  if (url) {
    return {
      url,
      target,
      rel
    };
  } else {
    return { anchor };
  }
}
function addEntity(editorState, targetSelection, entityData) {
  const entityKey = createEntity(editorState, entityData);
  const oldSelection = editorState.getSelection();
  const newContentState = Modifier.applyEntity(
    editorState.getCurrentContent(),
    targetSelection,
    entityKey
  ).set("selectionAfter", oldSelection);
  return EditorState.push(editorState, newContentState, "apply-entity");
}
const hasLinksInBlock = (block, contentState) => {
  return !!getLinkRangesInBlock(block, contentState).length;
};
const hasLinksInSelection = (editorState) => {
  return !!getSelectedLinks(editorState).length;
};
const getLinkDataInSelection = (editorState) => {
  const contentState = editorState.getCurrentContent();
  const selection = getSelection(editorState);
  const startKey = selection.getStartKey();
  const startOffset = selection.getStartOffset();
  const blockWithLinkAtBeginning = contentState.getBlockForKey(startKey);
  const linkKey = blockWithLinkAtBeginning.getEntityAt(startOffset);
  return linkKey ? contentState.getEntity(linkKey).getData() : {};
};
const removeLinksInSelection = (editorState) => {
  const selection = editorState.getSelection();
  const newEditorState = getSelectedLinks(editorState).reduce(
    (prevState, { key, range }) => removeLink(prevState, key, range),
    editorState
  );
  return EditorState.forceSelection(
    newEditorState,
    selection.merge({ anchorOffset: selection.getFocusOffset() })
  );
};
const getTextAlignment = (editorState, defaultAlignment = "left") => {
  const selection = getSelection(editorState);
  const currentContent = editorState.getCurrentContent();
  const contentBlock = currentContent.getBlockForKey(selection.getStartKey());
  const {
    data: { textAlignment }
  } = contentBlock.toJS();
  return textAlignment || defaultAlignment;
};
const setTextAlignment = (editorState, textAlignment) => {
  return mergeBlockData(editorState, { textAlignment });
};
const mergeBlockData = (editorState, data) => {
  const contentState = Modifier.mergeBlockData(
    editorState.getCurrentContent(),
    editorState.getSelection(),
    data
  );
  return EditorState.push(editorState, contentState, "change-block-data");
};
const getAnchorBlockData = (editorState) => {
  const anchorKey = editorState.getSelection().getAnchorKey();
  const block = editorState.getCurrentContent().getBlockForKey(anchorKey);
  return block.get("data").toJS();
};
const blockKeyToEntityKey = (editorState, blockKey) => {
  const block = editorState.getCurrentContent().getBlockForKey(blockKey);
  const entityKey = block.getEntityAt(0);
  return entityKey;
};
const setEntityData = (editorState, entityKey, data) => {
  if (entityKey) {
    const contentState = editorState.getCurrentContent();
    contentState.replaceEntityData(entityKey, __spreadValues({}, data));
  }
  return editorState;
};
const setBlockNewEntityData = (editorState, blockKey, data, type) => {
  const targetSelection = new SelectionState({
    anchorKey: blockKey,
    anchorOffset: 0,
    focusKey: blockKey,
    focusOffset: 1
  });
  return addEntity(editorState, targetSelection, {
    type,
    data,
    mutability: "IMMUTABLE"
  });
};
const isAtomicBlockFocused = (editorState) => {
  const selection = editorState.getSelection();
  const [anchorKey, focusKey] = [selection.getAnchorKey(), selection.getFocusKey()];
  const block = editorState.getCurrentContent().getBlockForKey(anchorKey).getType();
  return anchorKey === focusKey && block === "atomic";
};
const replaceWithEmptyBlock = (editorState, blockKey) => {
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(blockKey);
  const blockRange = new SelectionState({
    anchorKey: blockKey,
    anchorOffset: 0,
    focusKey: blockKey,
    focusOffset: block.getLength()
  });
  const withoutBlock = Modifier.removeRange(contentState, blockRange, "backward");
  const resetBlock = Modifier.setBlockType(
    withoutBlock,
    withoutBlock.getSelectionAfter(),
    "unstyled"
  );
  const newState = EditorState.push(editorState, resetBlock, "remove-range");
  return EditorState.forceSelection(newState, resetBlock.getSelectionAfter());
};
const setSelectionToBlock = (newEditorState, setEditorState, newActiveBlock) => {
  const editorState = newEditorState;
  const offsetKey = DraftOffsetKey.encode(newActiveBlock.getKey(), 0, 0);
  const node = document.querySelectorAll(`[data-offset-key="${offsetKey}"]`)[0];
  const selection = window.getSelection();
  const range = document.createRange();
  range.setStart(node, 0);
  range.setEnd(node, 0);
  selection == null ? void 0 : selection.removeAllRanges();
  selection == null ? void 0 : selection.addRange(range);
  setEditorState(
    EditorState.forceSelection(
      editorState,
      new SelectionState({
        anchorKey: newActiveBlock.getKey(),
        anchorOffset: 0,
        focusKey: newActiveBlock.getKey(),
        focusOffset: 0,
        isBackward: false
      })
    )
  );
};
const createBlockAndFocus = (editorState, data, pluginType) => {
  var _a;
  const { newBlock, newSelection, newEditorState } = createBlock(editorState, data, pluginType);
  (_a = window.getSelection()) == null ? void 0 : _a.removeAllRanges();
  return new Promise((resolve) => {
    setTimeout(() => {
      resolve({
        newEditorState: EditorState.forceSelection(newEditorState, newSelection),
        newBlock
      });
    }, 0);
  });
};
const addEmptyBlock = (direction, editorState) => {
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const currentBlock = contentState.getBlockForKey(selection.getEndKey());
  const blockMap = contentState.getBlockMap();
  const blocksBefore = blockMap.toSeq().takeUntil((v) => v === currentBlock);
  const blocksAfter = blockMap.toSeq().skipUntil((v) => v === currentBlock).rest();
  const newBlockKey = genKey();
  const newBlocks = [
    [currentBlock.getKey(), currentBlock],
    [
      newBlockKey,
      new ContentBlock({
        key: newBlockKey,
        type: "unstyled",
        text: "",
        characterList: List()
      })
    ]
  ];
  direction === "before" && newBlocks.reverse();
  const newBlockMap = blocksBefore.concat(newBlocks, blocksAfter).toOrderedMap();
  const newContentState = contentState.merge({
    blockMap: newBlockMap,
    selectionBefore: selection,
    selectionAfter: selection
  });
  const newEditorState = EditorState.push(editorState, newContentState, "insert-fragment");
  const newSelection = SelectionState.createEmpty(newBlockKey);
  return EditorState.forceSelection(newEditorState, newSelection);
};
const createBlock = (editorState, data, type) => {
  const currentEditorState = isAtomicBlockInSelection(editorState) ? addEmptyBlock("after", editorState) : editorState;
  const contentState = currentEditorState.getCurrentContent();
  const contentStateWithEntity = contentState.createEntity(
    type,
    "IMMUTABLE",
    cloneDeepWithoutEditorState(data)
  );
  const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
  const newEditorState = AtomicBlockUtils.insertAtomicBlock(currentEditorState, entityKey, " ");
  const recentlyCreatedKey = newEditorState.getSelection().getAnchorKey();
  const newBlock = newEditorState.getCurrentContent().getBlockBefore(recentlyCreatedKey);
  const newSelection = SelectionState.createEmpty(newBlock.getKey());
  return { newBlock, newSelection, newEditorState };
};
const deleteBlock = (editorState, blockKey) => {
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(blockKey);
  const previousBlock = contentState.getBlockBefore(blockKey) || block;
  const anchorOffset = previousBlock.getKey() === blockKey ? 0 : previousBlock.getText().length;
  const selectionRange = new SelectionState({
    anchorKey: previousBlock.getKey(),
    anchorOffset,
    focusKey: blockKey,
    focusOffset: block.getText().length,
    hasFocus: true
  });
  const newContentState = Modifier.removeRange(contentState, selectionRange, "forward");
  return EditorState.push(editorState, newContentState, "remove-range");
};
const deleteBlockText = (editorState, blockKey) => {
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(blockKey);
  const selectionRange = createSelection({
    blockKey,
    anchorOffset: 0,
    focusOffset: block.getText().length
  });
  const newContentState = Modifier.replaceText(contentState, selectionRange, "");
  return EditorState.push(editorState, newContentState, "remove-range");
};
const getSelectedBlocks = (editorState) => {
  const blocks = editorState.getCurrentContent().getBlocksAsArray();
  const selection = getSelection(editorState);
  const firstIndex = findIndex(blocks, (block) => block.getKey() === selection.getAnchorKey());
  const lastIndex = findLastIndex(blocks, (block) => block.getKey() === selection.getFocusKey());
  return blocks.slice(firstIndex, lastIndex + 1);
};
const isAtomicBlockInSelection = (editorState) => {
  return getSelectedBlocks(editorState).some((block) => block.getType() === "atomic");
};
const isTextBlockInSelection = (editorState) => {
  return getSelectedBlocks(editorState).some((block) => block.getType() !== "atomic");
};
const getSelectionRange = (editorState, block) => {
  const selection = getSelection(editorState);
  const blockKey = block.getKey();
  const anchorKey = selection.getAnchorKey();
  const focusKey = selection.getFocusKey();
  const anchorOffset = selection.getAnchorOffset();
  const focusOffset = selection.getFocusOffset();
  let range;
  if (anchorKey === blockKey && focusKey === blockKey) {
    range = [anchorOffset, focusOffset];
  } else if (anchorKey === blockKey) {
    range = [anchorOffset, block.getLength()];
  } else if (focusKey === blockKey) {
    range = [0, focusOffset];
  } else {
    range = [0, block.getLength()];
  }
  return range;
};
const isInSelectionRange = ([start, end], range) => {
  return !(start <= range[0] && end <= range[0]) && !(start >= range[1] && end >= range[1]);
};
function getSelectedLinks(editorState) {
  return flatMap(
    getSelectedBlocks(editorState),
    (block) => getSelectedLinksInBlock(block, editorState)
  );
}
function getSelectedLinksInBlock(block, editorState) {
  const selectionRange = getSelectionRange(editorState, block);
  return getLinkRangesInBlock(block, editorState.getCurrentContent()).filter((linkRange) => isInSelectionRange(selectionRange, linkRange)).map((linkRange) => ({
    key: block.getKey(),
    range: linkRange
  }));
}
function getLinkRangesInBlock(block, contentState) {
  const ranges = [];
  block.findEntityRanges(
    (value) => {
      const key = value.getEntity();
      return !!key && ["LINK", "ANCHOR"].includes(contentState.getEntity(key).getType());
    },
    (start, end) => ranges.push([start, end])
  );
  return ranges;
}
function removeLink(editorState, blockKey, [start, end]) {
  const selection = createSelection({ blockKey, anchorOffset: start, focusOffset: end });
  const newContentState = Modifier.removeInlineStyle(
    RichUtils.toggleLink(editorState, selection, null).getCurrentContent(),
    selection,
    "UNDERLINE"
  );
  return EditorState.push(editorState, newContentState, "change-inline-style");
}
function createEntity(editorState, { type, mutability = "MUTABLE", data }) {
  return editorState.getCurrentContent().createEntity(type, mutability, data).getLastCreatedEntityKey();
}
function getSelection(editorState) {
  let selection = editorState.getSelection();
  if (selection.getIsBackward()) {
    selection = new SelectionState({
      anchorKey: selection.getFocusKey(),
      anchorOffset: selection.getFocusOffset(),
      focusKey: selection.getAnchorKey(),
      focusOffset: selection.getAnchorOffset()
    });
  }
  return selection;
}
function getEditorContentSummary(contentState) {
  return getContentSummary(contentState);
}
const countByTypeField = (obj) => countBy(obj, (x) => x.type);
const getBlockTypePlugins = (blocks) => blocks.filter((block) => block.getType() !== "unstyled" && block.getType() !== "atomic");
const getEntities = (content, entityType) => {
  const entities = [];
  content.getBlockMap().forEach((block) => {
    block == null ? void 0 : block.findEntityRanges(
      (character) => {
        const char = character.getEntity();
        if (char) {
          const entity = content.getEntity(char);
          if (!entityType || entity.getType() === entityType) {
            entities.push(entity);
          }
        } else {
          entities.push({
            getType: () => "text",
            getData: () => ""
          });
        }
        return false;
      },
      () => {
      }
    );
  });
  return entities;
};
const calculateContentDiff = (prevState, newState, onCallbacks) => {
  const prevEntities = countByTypeField(getEntities(prevState.getCurrentContent()));
  const currEntities = countByTypeField(getEntities(newState.getCurrentContent()));
  const prevBlocks = prevState.getCurrentContent().getBlocksAsArray();
  const currBlocks = newState.getCurrentContent().getBlocksAsArray();
  const prevBlockPlugins = countByTypeField(getBlockTypePlugins(prevBlocks));
  const currBlockPlugins = countByTypeField(getBlockTypePlugins(currBlocks));
  const prevPluginsTotal = Object.assign(prevEntities, prevBlockPlugins);
  const currPluginsTotal = Object.assign(currEntities, currBlockPlugins);
  const pluginsDeleted = [];
  Object.keys(prevPluginsTotal).filter((type) => type !== "undefined").forEach((type) => {
    const deletedCount = prevPluginsTotal[type] - (currPluginsTotal[type] || 0);
    times(deletedCount, () => pluginsDeleted.push(type));
  });
  onCallbacks({ pluginsDeleted });
};
const createCalcContentDiff = (editorState) => {
  let prevState = editorState;
  return debounce((newState, { shouldCalculate, onCallbacks }) => {
    if (!shouldCalculate)
      return;
    calculateContentDiff(prevState, newState, onCallbacks);
    prevState = newState;
  }, 300);
};
function createLastChangeSelection(editorState) {
  const content = editorState.getCurrentContent();
  const selectionBefore = content.getSelectionBefore();
  return content.getSelectionAfter().merge({
    anchorKey: selectionBefore.getStartKey(),
    anchorOffset: selectionBefore.getStartOffset()
  });
}
function fixPastedLinks(editorState, { anchorTarget, relValue }) {
  const lastChangeSelection = createLastChangeSelection(editorState);
  const links = getSelectedLinks(setSelection(editorState, lastChangeSelection));
  const content = editorState.getCurrentContent();
  links.forEach(({ key: blockKey, range }) => {
    const block = content.getBlockForKey(blockKey);
    const entityKey = block.getEntityAt(range[0]);
    const data = entityKey && content.getEntity(entityKey).getData();
    const url = data.url || data.href;
    if (url) {
      content.replaceEntityData(entityKey, {
        url,
        target: anchorTarget,
        rel: relValue
      });
    }
  });
  return editorState;
}
function getFocusedBlockKey(editorState) {
  const selection = editorState.getSelection();
  if (selection.isCollapsed())
    return selection.getAnchorKey();
}
function getBlockEntityType(editorState, blockKey) {
  var _a;
  return (_a = getBlockEntity(editorState, blockKey)) == null ? void 0 : _a.getType();
}
function getBlockEntity(editorState, blockKey) {
  const contentState = editorState.getCurrentContent();
  const block = contentState.getBlockForKey(blockKey);
  const entityKey = block == null ? void 0 : block.getEntityAt(0);
  return entityKey ? contentState.getEntity(entityKey) : void 0;
}
function getBlockInfo(editorState, blockKey) {
  const entity = getBlockEntity(editorState, blockKey);
  const entityData = entity == null ? void 0 : entity.getData();
  const type = entity == null ? void 0 : entity.getType();
  return { type: type || "text", entityData };
}
function getBlockType(editorState) {
  const contentState = editorState.getCurrentContent();
  const blockKey = editorState.getSelection().getAnchorKey();
  const block = contentState.getBlockForKey(blockKey);
  return block == null ? void 0 : block.getType();
}
function setSelection(editorState, selection) {
  return EditorState.acceptSelection(editorState, selection);
}
const isTypeText = (blockType) => {
  return TEXT_TYPES.some((type) => type === blockType);
};
function indentSelectedBlocks(editorState, adjustment) {
  const maxDepth = 4;
  const selection = editorState.getSelection();
  const contentState = editorState.getCurrentContent();
  const startKey = selection.getStartKey();
  const endKey = selection.getEndKey();
  const blockMap = contentState.getBlockMap();
  const adjustBlockDepth = (block, adjustment2) => {
    let depth = block.getDepth() + adjustment2;
    depth = Math.max(0, Math.min(depth, maxDepth));
    return block.set("depth", depth);
  };
  const getBlocks = () => blockMap.toSeq().skipUntil((_, k) => k === startKey).takeUntil((_, k) => k === endKey).concat([[endKey, blockMap.get(endKey)]]);
  const blocks = getBlocks().filter((block) => !!block && isTypeText(block.getType())).map((block) => !!block && adjustBlockDepth(block, adjustment));
  const withAdjustment = contentState.merge({
    blockMap: blockMap.merge(blocks),
    selectionBefore: selection,
    selectionAfter: selection
  });
  return EditorState.push(editorState, withAdjustment, "adjust-depth");
}
function setForceSelection(editorState, selection) {
  return EditorState.forceSelection(editorState, selection);
}
function insertString(editorState, string) {
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  const newContentState = Modifier.replaceText(contentState, selectionState, string);
  return EditorState.push(editorState, newContentState, "insert-string");
}
function getCharacterBeforeSelection(editorState) {
  let character;
  const selectionState = editorState.getSelection();
  const start = selectionState.getStartOffset() - 1;
  if (start >= 0) {
    const anchorKey = selectionState.getAnchorKey();
    const contentState = editorState.getCurrentContent();
    const currentContentBlock = contentState.getBlockForKey(anchorKey);
    character = currentContentBlock.getText().slice(start, start + 1);
  }
  return character;
}
function deleteCharacterBeforeCursor(editorState) {
  let newState;
  const contentState = editorState.getCurrentContent();
  const selectionState = editorState.getSelection();
  if (selectionState.isCollapsed()) {
    const start = selectionState.getStartOffset() - 1;
    const newSelection = selectionState.set("anchorOffset", start);
    const newContentState = Modifier.replaceText(contentState, newSelection, "");
    newState = EditorState.push(editorState, newContentState, "delete-string");
  }
  return newState;
}
function isPluginFocused(block, selection) {
  return block.getKey() === selection.getAnchorKey();
}
function isCursorAtFirstLine(editorState) {
  const selection = editorState.getSelection();
  return selection.isCollapsed() && editorState.getCurrentContent().getBlockMap().first().getKey() === selection.getFocusKey();
}
function isCursorAtStartOfContent(editorState) {
  const isStartOfLine = editorState.getSelection().getFocusOffset() === 0;
  return isStartOfLine && isCursorAtFirstLine(editorState);
}
const hasBlockType = (blockType, editorState) => {
  const currentBlockType = editorState.getCurrentContent().getBlockForKey(editorState.getSelection().getStartKey()).getType();
  return blockType === currentBlockType;
};
function selectAllContent(editorState, forceSelection) {
  const currentContent = editorState.getCurrentContent();
  const selection = editorState.getSelection().merge({
    anchorKey: currentContent.getFirstBlock().getKey(),
    anchorOffset: 0,
    focusOffset: currentContent.getLastBlock().getText().length,
    focusKey: currentContent.getLastBlock().getKey()
  });
  const setSelectionFunction = forceSelection ? EditorState.forceSelection : EditorState.acceptSelection;
  const newEditorState = setSelectionFunction(editorState, selection);
  return newEditorState;
}
function setNativeSelectionToBlock(block) {
  const offsetKey = DraftOffsetKey.encode(block.getKey(), 0, 0);
  const node = document.querySelectorAll(`[data-offset-key="${offsetKey}"]`)[0];
  const selection = window.getSelection();
  const range = document.createRange();
  range.setStart(node, 0);
  range.setEnd(node, 0);
  selection == null ? void 0 : selection.removeAllRanges();
  selection == null ? void 0 : selection.addRange(range);
}
function insertTextToBlock(block, contentState, text) {
  const key = block.getKey();
  const newLineTarget = SelectionState.createEmpty(key).set("anchorOffset", 0).set("focusOffset", 0);
  return Modifier.insertText(contentState, newLineTarget, text);
}
function mergeBlocksText(blocks) {
  let text = "";
  const blocksLength = blocks.length;
  const withNewLine = (blockIndex) => blockIndex < blocksLength - 1;
  blocks.forEach(
    (block, i) => withNewLine(i) ? text += block.getText() + "\n" : text += block.getText()
  );
  return text;
}
function replaceSelectedBlocksWithFragment(firstKey, lastKey, contentState) {
  const fragment = BlockMapBuilder.createFromArray([...createEmptyBlocks(3)]);
  const target = new SelectionState({
    anchorKey: firstKey,
    anchorOffset: 0,
    focusKey: lastKey,
    focusOffset: contentState.getBlockForKey(lastKey).getLength()
  });
  return Modifier.replaceWithFragment(contentState, target, fragment);
}
function createEmptyBlocks(numOfBlocks = 1) {
  const blocks = [];
  for (let i = 0; i < numOfBlocks; i++) {
    blocks.push(ContentState.createFromText("").getBlockMap().first());
  }
  return blocks;
}
function setBlockType(contentState, blockType) {
  return Modifier.setBlockType(contentState, contentState.getSelectionAfter(), blockType);
}
function toggleBlockTypeWithSpaces(editorState, blockType) {
  if (hasBlockType(blockType, editorState)) {
    return RichUtils.toggleBlockType(editorState, blockType);
  }
  const selection = getSelection(editorState);
  const contentState = editorState.getCurrentContent();
  const firstKey = selection.getStartKey();
  const lastKey = selection.getEndKey();
  const selectedBlocks = getSelectedBlocks(editorState);
  const text = selectedBlocks.length ? mergeBlocksText(selectedBlocks) : selectedBlocks[0].getText();
  let newContentState = replaceSelectedBlocksWithFragment(firstKey, lastKey, contentState);
  const block = newContentState.getBlockAfter(firstKey);
  newContentState = insertTextToBlock(block, newContentState, text);
  newContentState = setBlockType(newContentState, blockType);
  const newSelection = createSelection({
    blockKey: (block == null ? void 0 : block.getKey()) || firstKey,
    anchorOffset: 0,
    focusOffset: 0
  });
  const newEditorState = EditorState.push(editorState, newContentState, "change-block-type");
  return EditorState.forceSelection(newEditorState, newSelection);
}

export { blockKeyToEntityKey, cloneDeepWithoutEditorState, createBlock, createBlockAndFocus, createCalcContentDiff, createEntity, createLinkEntityData, createSelection, deleteBlock, deleteBlockText, deleteCharacterBeforeCursor, fixPastedLinks, getAnchorBlockData, getBlockAtStartOfSelection, getBlockEntity, getBlockEntityType, getBlockInfo, getBlockType, getCharacterBeforeSelection, getDraftInlineStyle, getEditorContentSummary, getEntities, getEntityData, getFocusedBlockKey, getLinkDataInSelection, getLinkRangesInBlock, getSelectedBlocks, getSelectionRange, getTextAlignment, hasBlockType, hasInlineStyle, hasLinksInBlock, hasLinksInSelection, indentSelectedBlocks, insertCustomLink, insertLinkAtCurrentSelection, insertLinkInPosition, insertString, isAtomicBlockFocused, isAtomicBlockInSelection, isCursorAtFirstLine, isCursorAtStartOfContent, isInSelectionRange, isPluginFocused, isTextBlockInSelection, isTypeText, mergeBlockData, removeLinksInSelection, replaceWithEmptyBlock, selectAllContent, setBlockNewEntityData, setEntityData, setForceSelection, setNativeSelectionToBlock, setSelection, setSelectionToBlock, setTextAlignment, toggleBlockTypeWithSpaces, updateLinkAtCurrentSelection };
//# sourceMappingURL=draftUtils.js.map
